/**
 * RWBR — THE BOOTH: WHERE THEY ARE. D-EZ-52.
 *
 * Howard, 18 Aug 2026: *"id like to lift the tokens even more on commentary and
 * allow them to talk a little more. Theyre still a little short on ambience."*
 *
 * ===========================================================================
 * THE TOKENS WERE NEVER THE THING STOPPING THEM
 * ===========================================================================
 *
 * Three ceilings sat between the booth and a line with any atmosphere in it, and
 * the token budget was the LOOSEST of them. In the order they actually bind:
 *
 *  1. `verify.ts` refused anything over 320 characters or three sentences. A
 *     longer line was not trimmed — it was REJECTED, and the beat fell back to
 *     the engine's flat sentence. Raising the token ceiling on its own would
 *     have bought more rejections and *worse* commentary.
 *  2. The role briefs asked for "one or two sentences".
 *  3. **AND THE BOOTH HAD NEVER BEEN TOLD WHERE IT WAS.** This is the one that
 *     matters, and it is what this file fixes.
 *
 * A beat's payload was the turn number, the angle, at most three facts and a
 * list of driver names. Not the track. Not the length of it. Not what kind of
 * racetrack it is, how far into the race they are, or how many people are in the
 * grandstand. And D-003 forbids invention — correctly — so a commentator who
 * mentioned Bristol, or concrete, or a half-mile, would have been making it up.
 *
 * The booth was not short on ambience because it was terse. **It was short on
 * ambience because it was blindfolded.** Everything below is read from the track
 * card and the race state; nothing is invented, and the no-invention rule is
 * untouched.
 *
 * ===========================================================================
 * ⚠ AND THE TRAP THIS FILE EXISTS TO AVOID
 * ===========================================================================
 *
 * `verifyLine` rejects any capitalised word that is not a known name — that is
 * what stops a model inventing a driver. Hand the booth a setting and let it say
 * "Bristol" and the gate throws the line out as a fabricated surname, so the
 * feature would have made the commentary *worse* in a way that looks like the
 * model being bad at its job.
 *
 * `settingWords` is the answer: every proper noun in the setting, handed to the
 * verifier's allow-list alongside the cast's own names. A word is sayable
 * because it is TRUE and was given, not because the gate got looser.
 */

import type { RaceResult } from '../race.ts';
import type { RaceState } from '../types.ts';

/** Everything true about where and when this race is, and nothing else. */
export interface BoothSetting {
  raceName: string;
  /** The track, era stamp removed and no longer shouting. */
  track: string;
  /** 'Short Track' · 'Superspeedway' · 'Intermediate Track' · 'Road Course'. */
  category: string;
  location: string | null;
  /** Miles, off the card. Null where the card prints none. */
  lengthMiles: number | null;
  /** Grandstand capacity as printed. Null on the road courses. */
  capacity: number | null;
  /** Laps in one turn, where the register knows. A turn is 10 miles (p.ii). */
  lapsPerTurn: number | null;
  /** Which turn this beat is on, and how many there are. */
  turn: number;
  totalTurns: number;
  /** Roughly how far through — "early", "midrace", "late", "the end". */
  stage: RaceStage;
  /** How many cars took the green. */
  fieldSize: number;
  /** How many are still running at this point in the tape. */
  stillRunning: number;
}

export type RaceStage = 'the opening laps' | 'the early stint' | 'midrace' | 'the closing stint' | 'the last laps';

/**
 * Where in the race a turn falls, in words a commentator would use.
 *
 * Bands rather than a percentage, because "we are 43% through" is not a thing
 * anybody says out loud and a model handed a number will say the number.
 *
 * ⚠ NO STAGE MAY CONTAIN THE WORD "MIDDLE", AND THAT IS NOT A STYLE NOTE.
 *
 * This band was called "the middle stint" and Howard caught what it did, 21 Aug
 * 2026, reading a beat about a car sitting seventh:
 *
 *     "The 88 has led nine turns in this middle stint"
 *
 *     *"is it mistakenly counting the 7 spot as leading the race or is it being
 *      clear that it is leading the middle group?"*
 *
 * The arithmetic was never wrong — `turnsLed` increments only for `i === 0`, the
 * actual race leader, so the 88 had genuinely led nine turns earlier and had
 * since dropped to seventh. But TOP, MIDDLE and BOTTOM are the three most
 * load-bearing words on the board, and "middle" used as a unit of TIME in a
 * sentence about LEADING, on a screen showing a MIDDLE group, cannot be read
 * the one way it was meant.
 *
 * "midrace" is what a broadcast actually says and is one word, so there is no
 * sentence in which it can be parsed as a place. None of the other four bands
 * collide: nothing on the board is called opening, early, closing or last.
 */
export function stageOf(turn: number, totalTurns: number): RaceStage {
  if (totalTurns <= 0) return 'the opening laps';
  const through = turn / totalTurns;
  if (turn <= 2) return 'the opening laps';
  if (through < 0.35) return 'the early stint';
  if (through < 0.7) return 'midrace';
  if (through < 0.92) return 'the closing stint';
  return 'the last laps';
}

/**
 * "2000s BRISTOL MOTOR SPEEDWAY" → "Bristol Motor Speedway".
 *
 * The era stamp is on every published card and a commentator does not say a year
 * inside a track's name. Shouted names are re-cased; a properly-cased one is
 * left alone.
 */
export function trackInSpeech(name: string): string {
  const bare = name.replace(/^\s*(19|20)\d{2}s?\s+/, '').trim();
  if (bare.length === 0) return name;
  if (bare !== bare.toUpperCase()) return bare;
  return bare
    .toLowerCase()
    .split(' ')
    .map((w) => (w.length === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

export interface SettingInput {
  result: RaceResult;
  /** The turn the beat is on. */
  turn: number;
  /** From `RaceCall`. Null when the register could not work it out. */
  lapsPerTurn: number | null;
  /** The state at this point in the tape, when the caller has it. */
  stateAtTurn?: RaceState;
}

export function settingFor(input: SettingInput): BoothSetting {
  const { result, turn } = input;
  const card = result.track;
  // A later caution must not tell the opening booth how long this race will run.
  const totalTurns = Math.max(result.scheduledTurns, turn);
  const fieldSize = result.state.grid.length + result.state.retired.length;
  const state = input.stateAtTurn ?? (turn >= result.turnsRun ? result.state : result.turns[turn - 1]?.state);

  return {
    raceName: result.state.raceName,
    track: trackInSpeech(card.name),
    category: card.category,
    location: card.location ?? null,
    lengthMiles: card.lengthMiles ?? null,
    capacity: card.capacity ?? null,
    lapsPerTurn: input.lapsPerTurn,
    turn,
    totalTurns,
    stage: stageOf(turn, totalTurns),
    // The field as it lined up, not as it stands — `retired` is where the rest
    // went, so the two together are the green-flag count.
    fieldSize,
    stillRunning: turn === 0 ? fieldSize : state?.grid.length ?? fieldSize,
  };
}

/**
 * ⚠ EVERY PROPER NOUN THE BOOTH IS NOW ENTITLED TO SAY.
 *
 * Handed to `verifyLine`'s allow-list. Without this the gate reads "Bristol" as
 * an invented surname and refuses the line — see the header. Split on
 * whitespace so "Bristol Motor Speedway" makes all three words sayable
 * individually, which is how a commentator actually uses a track name.
 */
export function settingWords(setting: BoothSetting): string[] {
  const out = new Set<string>();
  const add = (text: string | null) => {
    if (!text) return;
    for (const word of text.split(/[\s,]+/)) {
      const clean = word.replace(/[^A-Za-z'’.-]/g, '');
      if (clean.length > 1) out.add(clean);
    }
  };
  add(setting.raceName);
  add(setting.track);
  add(setting.category);
  add(setting.location);
  add(setting.stage);
  return [...out];
}

/**
 * ⚠ AND EVERY NUMBER, for the same reason.
 *
 * `verifyLine` refuses a line containing a number no fact supports — that is
 * what stops a model inventing a lap count. The setting's numbers are true and
 * were handed over, so they belong in the allow-list; without them, a
 * commentator who correctly said "half a mile" or "turn 19 of 45" would have the
 * line thrown out.
 *
 * The rounded forms go in too. A person says "sixty thousand", not "59,847",
 * and a gate that only knows the exact figure refuses the human sentence and
 * keeps the robotic one.
 */
export function settingNumbers(setting: BoothSetting): number[] {
  const out = new Set<number>();
  const add = (n: number | null | undefined) => {
    if (n === null || n === undefined || !Number.isFinite(n)) return;
    out.add(n);
    out.add(Math.round(n));
  };
  add(setting.lengthMiles);
  add(setting.capacity);
  if (setting.capacity) {
    add(Math.round(setting.capacity / 1000));
    add(Math.round(setting.capacity / 1000) * 1000);
  }
  add(setting.lapsPerTurn);
  add(setting.turn);
  add(setting.totalTurns);
  add(setting.fieldSize);
  add(setting.stillRunning);
  // Turns remaining is arithmetic on two numbers already here, and it is the
  // single most natural thing for a commentator to say late in a race.
  add(setting.totalTurns - setting.turn);
  return [...out];
}
